import type { ReactNode } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createContext } from 'use-context-selector';
import type { TutorialListItemT, TutorialServiceT, TutorialStepT } from '../../../common/types/types';
import type { TutorialProviderT } from '../types';
export const TutorialContext = createContext<TutorialServiceT>({
  onTutorialReplay: () => () => {},
  addStep: () => {},
  next: () => {},
  hideStep: () => {},
  showStep: () => {},
  triggerReplay: () => {},
  tutorialList: [],
  currentActiveTutorial: null
});
type PropsT = {
  // This is provided by consumer of this studio for rendering tutorial.
  tutorialProvider?: TutorialProviderT;
  children: ReactNode;
};
export const TutorialProvider = ({
  tutorialProvider,
  children
}: PropsT) => {
  const rootNodeMapRef = useRef(new Map<string, {
    callback: () => void;
  } & Omit<TutorialListItemT, 'id'>>());
  const replayStatusRef = useRef(new Map<string, boolean | undefined>());
  const [tutorialList, setTutorialList] = useState<Array<TutorialListItemT>>([]);
  const [currentActiveTutorial, setCurrentActiveTutorial] = useState<string | null>(null);
  const onTutorialReplay = useCallback((rootNodeId: string, section: TutorialListItemT['section'], name: TutorialListItemT['name'], callback: () => void, isTutorialReady?: TutorialListItemT['isTutorialReady']) => {
    const nodeMapToTutorialList: () => TutorialListItemT[] = () => [...rootNodeMapRef.current.entries()].map(([id, entry]) => ({
      name: entry.name,
      section: entry.section,
      id,
      isTutorialReady: entry.isTutorialReady
    }));
    const cleanUp = () => {
      rootNodeMapRef.current.delete(rootNodeId);
      setTutorialList(nodeMapToTutorialList());
    };
    if (rootNodeMapRef.current.has(rootNodeId)) {
      console.warn(`Callback is already registered for tutorial ${rootNodeId}`);
      return cleanUp;
    }
    rootNodeMapRef.current.set(rootNodeId, {
      name,
      section,
      callback,
      isTutorialReady
    });
    setTutorialList(nodeMapToTutorialList());
    return cleanUp;
  }, []);
  const triggerReplay = useCallback((rootStepId: string) => {
    rootNodeMapRef.current.get(rootStepId)?.callback();
  }, []);

  // Verifies the add step can be called and maintain the internal tutorial state. eg. Currently running tutorial.
  const providerAddStep = useCallback(async (tutorial: TutorialStepT, rootId: string) => {
    if (!rootNodeMapRef.current.has(tutorial.id) && tutorial.isRoot) {
      console.warn(`Replay not registered for tutorial ${tutorial.id}`);
    }
    if (currentActiveTutorial && currentActiveTutorial !== rootId) {
      return;
    }
    if (tutorial.isRoot && tutorial.isReplay) {
      replayStatusRef.current.set(tutorial.id, true);
    }
    const isSuccess = await tutorialProvider?.addStep({
      ...tutorial,
      isReplay: replayStatusRef.current.get(rootId) ?? false,
      onNext: () => {
        const isLastReplayTutorial = replayStatusRef.current.get(rootId) && !tutorial.nextId;
        if (isLastReplayTutorial) {
          replayStatusRef.current.set(rootId, false);
        }
        if (!tutorial.nextId) {
          setCurrentActiveTutorial(null);
        }
      }
    });
    if (!currentActiveTutorial && isSuccess) {
      setCurrentActiveTutorial(rootId);
    }
  }, [tutorialProvider, currentActiveTutorial]);
  const next = useMemo(() => {
    return tutorialProvider?.next ?? (() => {});
  }, [tutorialProvider]);
  const showStep = useMemo(() => {
    return tutorialProvider?.showStep ?? (() => {});
  }, [tutorialProvider]);
  const hideStep = useMemo(() => {
    return tutorialProvider?.hideStep ?? (() => {});
  }, [tutorialProvider]);
  return <TutorialContext.Provider value={{
    addStep: providerAddStep,
    onTutorialReplay,
    next,
    showStep,
    hideStep,
    triggerReplay,
    tutorialList,
    currentActiveTutorial
  }}>
      {children}
    </TutorialContext.Provider>;
};